import { Component, input, OnInit, output } from '@angular/core';

import { AsyncValidatorFn, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { translate } from '@jsverse/transloco';
import { ErrorObject } from '@inmoment/the-kitchen';

import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

import { ErrorObjectUtils } from 'app/utils/error-object.utils';
import {
  ConfigurationService,
  SemantriaConfiguration,
} from '../../../../providers/semantria-api/configuration.service';

export interface CloneConfigurationDialogOptions {
  configuration: SemantriaConfiguration;
  nameValidator: AsyncValidatorFn;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-clone-configuration-dialog',
  templateUrl: './clone-configuration-dialog.component.html',
  styleUrls: ['./clone-configuration-dialog.component.scss'],
  standalone: false,
})
export class CloneConfigurationDialogComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly data = input<CloneConfigurationDialogOptions>();

  readonly modalClosed = output<SemantriaConfiguration>();

  originalConfig: SemantriaConfiguration;
  cloneConfigForm: UntypedFormGroup;
  configNameStatus: ErrorObject = ErrorObjectUtils.create();

  constructor(
    private formBuilder: UntypedFormBuilder,
    protected configService: ConfigurationService
  ) {}

  ngOnInit() {
    this.originalConfig = this.data().configuration;
    this.cloneConfigForm = this.formBuilder.group({
      configName: [
        '',
        Validators.compose([Validators.required]),
        Validators.composeAsync([this.data().nameValidator]),
      ],
    });
    this.nameControl.statusChanges.subscribe((status) => {
      this.configNameStatus = ErrorObjectUtils.create();

      if (status === 'PENDING') {
        return;
      }

      if (this.nameControl.hasError('required')) {
        ErrorObjectUtils.setError(
          this.configNameStatus,
          translate('configurations.clone-dialog.errors.required')
        );
        return;
      }

      if (this.nameControl.hasError('minLength')) {
        ErrorObjectUtils.setError(
          this.configNameStatus,
          translate('configurations.clone-dialog.errors.min-length', {
            charLength: this.nameControl.errors.minLength.minLength,
          })
        );
        return;
      }

      if (this.nameControl.hasError('maxLength')) {
        ErrorObjectUtils.setError(
          this.configNameStatus,
          translate('configurations.clone-dialog.errors.max-length', {
            charLength: this.nameControl.errors.maxLength.maxLength,
          })
        );
        return;
      }

      if (this.nameControl.hasError('nameAlreadyExists')) {
        ErrorObjectUtils.setError(
          this.configNameStatus,
          translate('configurations.clone-dialog.errors.exists')
        );
      }
    });
  }

  cancel() {
    this.modalClosed.emit(null);
  }

  clone() {
    if (this.cloneConfigForm.valid) {
      const clone = JSON.parse(JSON.stringify(this.originalConfig));
      clone.name = this.cloneConfigForm.controls.configName.value;
      clone.cloned_from = clone.id;
      delete clone.id;
      this.modalClosed.emit(clone);
    }
  }

  get nameControl() {
    return this.cloneConfigForm.controls.configName;
  }
}
